import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import ContactsServices from '../../../Contacts/ContactsServices';
import useSocket from '../../../../Hooks/useSocket';
import useSocketSubscription from '../../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../../Hooks/useSocketEvent';
import useVisualHighlight from '../../../../Hooks/useVisualHighlight';
import { Resource, Action } from '../../../../Constants/socket';

/**
 * Custom hook for fetching and managing contacts data with pagination, search, filters, and sorting.
 * Follows the same pattern as useCredentialFetching for consistency.
 *
 * @param {Object} channel - The channel object containing the channel ID.
 * @param {Function} handleError - Optional error handler function.
 * @returns {Object} The contacts state and control functions.
 */
const useContactsFetching = (channel, handleError) => {
    const { t } = useTranslation();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: [5, 10, 25, 50, 100],
    });
    const [searchString, setSearchString] = useState('');
    const [filters, setFilters] = useState([]);  // Array for SharedFilterBar
    const [normalizedFilters, setNormalizedFilters] = useState({});  // Object for API
    const [sorter, setSorter] = useState({
        field: null,
        order: null,
    });

    const searchTimerRef = useRef(null);
    const isInitialMountRef = useRef(true);
    const { socket } = useSocket();

    // Visual highlights for real-time socket events
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Subscribe to channel room for CONTACT:CREATE and CONTACT:DELETE events
    useSocketSubscription(Resource.CHANNEL, channel?.id ? [channel.id] : null);

    // Subscribe to specific contact IDs for CONTACT:UPDATE events
    const contactIds = useMemo(() => contacts.map(c => c.id).filter(Boolean), [contacts]);
    useSocketSubscription(Resource.CONTACT, contactIds.length > 0 ? contactIds : null);

    const fetchContacts = useCallback(async (page, pageSize, search, filterParams, sortField, sortOrder) => {
        if (!channel?.id) return;

        const isInitial = page === 1 && !contacts.length;
        if (isInitial) {
            setIsInitialLoading(true);
        }
        setLoading(true);

        try {
            const params = {
                page: page || pagination.current,
                size: pageSize || pagination.pageSize,
                search: search !== undefined ? search : searchString,
                filters: {
                    channel_id: channel.id,
                    ...(filterParams !== undefined ? filterParams : normalizedFilters),
                },
                sort_by: sortField !== undefined ? sortField : sorter.field,
                sort_direction: sortOrder !== undefined ? sortOrder : sorter.order,
            };

            const response = await ContactsServices.getContacts(params);

            const items = response?.data || [];
            setContacts(items);

            const responsePage = response.metadata?.page;
            const responsePageSize = response.metadata?.page_size;
            const responseTotal = response.metadata?.total;

            setPagination(prev => ({
                ...prev,
                current: responsePage,
                pageSize: responsePageSize,
                total: responseTotal,
            }));

            // Update sorter with API's default sort values
            const apiSortBy = response.metadata?.sort_by;
            const apiSortDirection = response.metadata?.sort_direction;

            if (apiSortBy || apiSortDirection) {
                setSorter({
                    field: apiSortBy || null,
                    order: apiSortDirection || null,
                });

                // On initial load, if API returns a default sort, initialize filters with that field
                if (isInitial && apiSortBy && filters.length === 0) {
                    const initialFilter = {
                        id: `${apiSortBy}-${Date.now()}`,
                        fieldKey: apiSortBy,
                        value: null,
                    };
                    setFilters([initialFilter]);
                }
            }
        } catch (error) {
            if (handleError) {
                handleError(error);
            } else {
                message.error(t('failed_to_load_contacts'));
            }
            setContacts([]);
        } finally {
            setLoading(false);
            if (isInitial) {
                setIsInitialLoading(false);
            }
        }
    }, [channel?.id, pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order, contacts.length, handleError, t, filters.length]);

    const refetchContacts = useCallback(() => {
        fetchContacts(pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order);
    }, [fetchContacts, pagination.current, pagination.pageSize, searchString, normalizedFilters, sorter.field, sorter.order]);

    // Socket Handlers
    const handleContactCreated = useCallback((data) => {
        refetchContacts();
        if (data?.id && data?.server_datetime && socket) {
            socket.subscribeToResource(Resource.CONTACT, [data.id], data.server_datetime);
        }
        if (data?.id) highlightRow(data.id);
    }, [refetchContacts, socket, highlightRow]);

    const handleContactUpdated = useCallback((data) => {
        setContacts(prev => prev.map(contact => contact.id === data.id ? { ...contact, ...data } : contact));
        if (data?.id) highlightRow(data.id);
    }, [highlightRow]);

    const handleContactDeleted = useCallback(() => {
        refetchContacts();
    }, [refetchContacts]);

    useSocketEvent(Resource.CONTACT, Action.CREATE, handleContactCreated);
    useSocketEvent(Resource.CONTACT, Action.UPDATE, handleContactUpdated);
    useSocketEvent(Resource.CONTACT, Action.DELETE, handleContactDeleted);

    const handleTableChange = useCallback((newPagination, tableFilters, tableSorter) => {
        const sortField = tableSorter?.field || null;
        const sortOrder = tableSorter?.order === 'ascend' ? 'asc' : tableSorter?.order === 'descend' ? 'desc' : null;

        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        setSorter({
            field: sortField,
            order: sortOrder,
        });

        fetchContacts(newPagination.current, newPagination.pageSize, searchString, normalizedFilters, sortField, sortOrder);
    }, [fetchContacts, searchString, normalizedFilters]);

    const handleSearch = useCallback((value) => {
        // Clear any existing timer
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        searchTimerRef.current = setTimeout(() => {
            setSearchString(value);
            setPagination(prev => ({ ...prev, current: 1 }));
            fetchContacts(1, pagination.pageSize, value, normalizedFilters, sorter.field, sorter.order);
        }, 500);
    }, [fetchContacts, pagination.pageSize, normalizedFilters, sorter.field, sorter.order]);

    const handleFilterChange = useCallback((filtersArray, normalizedFiltersObject) => {
        setFilters(filtersArray);  // Store the array for SharedFilterBar
        setNormalizedFilters(normalizedFiltersObject);  // Store the object for API
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchContacts(1, pagination.pageSize, searchString, normalizedFiltersObject, sorter.field, sorter.order);
    }, [fetchContacts, pagination.pageSize, searchString, sorter.field, sorter.order]);

    // Initial fetch when channel changes
    useEffect(() => {
        if (channel?.id) {
            isInitialMountRef.current = false;
            fetchContacts(1, pagination.pageSize);
        }

        // Cleanup search timer on unmount
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        contacts,
        setContacts,
        loading,
        isInitialLoading,
        pagination,
        searchString,
        filters,
        sorter,
        fetchContacts,
        refetchContacts,
        handleTableChange,
        handleSearch,
        handleFilterChange,
        setSearchString,
        setNormalizedFilters,
        setSorter,
        getHighlightClass
    };
};

export default useContactsFetching;
